<template>
  <el-drawer
    :visible.sync="drawerVisible"
    direction="rtl"
    :size="drawerSize"
    :with-header="false"
    :wrapper-closable="false"
    append-to-body
    @closed="resetState"
  >
    <div class="drawer-shell">
      <header class="drawer-header">
        <div>
          <div class="drawer-kicker">持仓风险监控设置</div>
          <h2>配置持仓上下文监控</h2>
          <p>只调整启停与运行时间；真实持仓、模拟持仓、第一本账范围和风险阈值不会被修改。</p>
        </div>
        <el-button type="text" icon="el-icon-close" class="drawer-close" @click="drawerVisible=false">关闭</el-button>
      </header>

      <div class="drawer-body" v-loading="loading">
        <el-alert
          title="保存不会立即执行任务或发送飞书；独立通知调度进程会在下一轮轮询中同步新时间。"
          type="info"
          :closable="false"
          show-icon
          class="block"
        />

        <el-alert
          v-if="errorText"
          :title="errorText"
          type="error"
          :closable="false"
          show-icon
          class="block"
        />

        <el-descriptions v-if="optionsReady" :column="1" border size="small" class="block">
          <el-descriptions-item label="监控范围">真实持仓 + 模拟持仓 + 第一本账上下文</el-descriptions-item>
          <el-descriptions-item label="通知策略">仅风险首次出现、变化或解除时发送</el-descriptions-item>
          <el-descriptions-item label="风险阈值">保持当前生产配置，不在本页面修改</el-descriptions-item>
          <el-descriptions-item label="当前时间">{{ currentScheduleText }}</el-descriptions-item>
        </el-descriptions>

        <el-divider content-position="left">编辑设置</el-divider>

        <el-form label-width="92px" size="small">
          <el-form-item label="启用状态">
            <el-radio-group v-model="form.enabled" @change="invalidatePreview">
              <el-radio-button :label="true">启用</el-radio-button>
              <el-radio-button :label="false">停用</el-radio-button>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="运行时间">
            <div class="time-editor">
              <el-time-picker
                v-model="timeToAdd"
                value-format="HH:mm"
                format="HH:mm"
                placeholder="选择时间"
                :clearable="true"
              />
              <el-button type="primary" plain @click="addTime">添加</el-button>
            </div>
            <div v-if="form.scheduleTimes.length" class="time-tags">
              <el-tag
                v-for="item in form.scheduleTimes"
                :key="item"
                closable
                effect="plain"
                @close="removeTime(item)"
              >{{ item }}</el-tag>
            </div>
            <div v-else class="empty-tip">当前未选择运行时间</div>
            <div class="form-tip">最多 12 个时间点；启用时至少保留一个时间。</div>
          </el-form-item>

          <el-form-item label="通知策略">
            <el-tag type="warning" effect="plain">仅异常通知</el-tag>
            <div class="form-tip">该策略已锁定，避免正常结果重复刷屏。</div>
          </el-form-item>
        </el-form>

        <div class="actions">
          <el-button
            type="primary"
            plain
            :loading="previewLoading"
            :disabled="!canPreview"
            @click="runPreview"
          >预览变更</el-button>
          <span>预览有效期 15 分钟，修改设置后需重新预览。</span>
        </div>

        <template v-if="preview">
          <el-divider content-position="left">保存前确认</el-divider>
          <el-alert
            :title="previewTitle"
            :type="changedCount > 0 ? 'success' : 'info'"
            :closable="false"
            show-icon
            class="block"
          />
          <el-descriptions :column="1" border size="small" class="block">
            <el-descriptions-item label="目标状态">{{ form.enabled ? '启用' : '停用' }}</el-descriptions-item>
            <el-descriptions-item label="目标时间">{{ targetScheduleText }}</el-descriptions-item>
            <el-descriptions-item label="通知策略">仅异常通知（保持不变）</el-descriptions-item>
            <el-descriptions-item label="预览失效">{{ displayTime(preview.preview_expires_at) }}</el-descriptions-item>
          </el-descriptions>
          <el-button
            type="primary"
            :loading="applyLoading"
            :disabled="!canApply"
            @click="saveSettings"
          >确认保存</el-button>
        </template>

        <el-alert
          v-if="applyResult"
          title="持仓监控设置已保存并完成精确回读。"
          type="success"
          :closable="false"
          show-icon
          class="block result"
        />
      </div>
    </div>
  </el-drawer>
</template>

<script>
import notificationTaskGroupApi from '@/api/notificationTaskGroup'

const GROUP_ID = 'position_context_watch'

export default {
  name: 'PositionContextWatchSettingsDialog',
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      previewLoading: false,
      applyLoading: false,
      options: null,
      preview: null,
      applyResult: null,
      errorText: '',
      timeToAdd: '',
      idempotencyKey: '',
      previewFingerprint: '',
      form: { enabled: true, scheduleTimes: [] }
    }
  },
  computed: {
    drawerVisible: {
      get() { return this.visible },
      set(value) { this.$emit('update:visible', value) }
    },
    drawerSize() { return typeof window !== 'undefined' && window.innerWidth <= 760 ? '100%' : '620px' },
    optionsReady() { return Boolean(this.options && this.options.ok === true) },
    tasks() { return this.optionsReady && Array.isArray(this.options.tasks) ? this.options.tasks : [] },
    currentScheduleText() {
      const values = this.extractTimes(this.tasks)
      return values.length ? `每日 ${values.join('、')}` : '尚未配置'
    },
    normalizedTimes() { return this.normalizeTimes(this.form.scheduleTimes) },
    canPreview() {
      if (!this.optionsReady) return false
      return !this.form.enabled || this.normalizedTimes.length > 0
    },
    fingerprint() {
      return JSON.stringify({ enabled: this.form.enabled, schedule_times: this.normalizedTimes })
    },
    changedCount() { return Number((this.preview && this.preview.changed_row_count) || 0) },
    previewMatches() { return Boolean(this.preview && this.previewFingerprint === this.fingerprint) },
    previewExpired() {
      const value = this.preview && this.preview.preview_expires_at
      if (!value) return true
      const time = new Date(value).getTime()
      return !Number.isFinite(time) || Date.now() > time
    },
    canApply() { return Boolean(this.preview && this.preview.ok === true && this.changedCount > 0 && this.previewMatches && !this.previewExpired) },
    previewTitle() { return this.changedCount > 0 ? '将更新 1 个持仓风险监控任务。' : '当前配置与目标配置一致，无需保存。' },
    targetScheduleText() { return this.normalizedTimes.length ? `每日 ${this.normalizedTimes.join('、')}` : '无运行时间' }
  },
  watch: {
    visible(value) { if (value) this.loadOptions() }
  },
  methods: {
    resetState() {
      this.loading = false
      this.previewLoading = false
      this.applyLoading = false
      this.options = null
      this.preview = null
      this.applyResult = null
      this.errorText = ''
      this.timeToAdd = ''
      this.idempotencyKey = ''
      this.previewFingerprint = ''
      this.form = { enabled: true, scheduleTimes: [] }
    },
    async loadOptions() {
      this.resetState()
      this.loading = true
      try {
        const payload = await notificationTaskGroupApi.getSettingsOptions(GROUP_ID)
        this.options = payload || null
        const tasks = this.options && Array.isArray(this.options.tasks) ? this.options.tasks : []
        this.form = {
          enabled: tasks.length ? tasks.every(item => item.status === 'active') : true,
          scheduleTimes: this.extractTimes(tasks)
        }
      } catch (error) {
        this.errorText = this.readError(error, '加载持仓监控设置失败。')
      } finally {
        this.loading = false
      }
    },
    extractTimes(tasks) {
      const values = []
      ;(Array.isArray(tasks) ? tasks : []).forEach(task => {
        String(task.schedule || '').split(',').forEach(item => values.push(item))
      })
      return this.normalizeTimes(values)
    },
    normalizeTimes(values) {
      return [...new Set((Array.isArray(values) ? values : [])
        .map(value => String(value || '').trim())
        .filter(value => /^([01]\d|2[0-3]):[0-5]\d$/.test(value)))].sort()
    },
    addTime() {
      if (!this.timeToAdd) return
      const values = this.normalizeTimes([...this.form.scheduleTimes, this.timeToAdd])
      if (values.length > 12) {
        this.$message.warning('最多只能设置 12 个运行时间')
        return
      }
      this.form.scheduleTimes = values
      this.timeToAdd = ''
      this.invalidatePreview()
    },
    removeTime(value) {
      this.form.scheduleTimes = this.form.scheduleTimes.filter(item => item !== value)
      this.invalidatePreview()
    },
    invalidatePreview() {
      this.preview = null
      this.applyResult = null
      this.errorText = ''
      this.previewFingerprint = ''
    },
    makeIdempotencyKey() {
      return `position-watch-settings-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    },
    intent(includeKey = true) {
      const payload = {
        schedule_times: this.normalizedTimes,
        enabled: Boolean(this.form.enabled),
        notification_policy: 'exception_only',
        scope_mode: null,
        scope_symbols: []
      }
      if (includeKey) payload.idempotency_key = this.idempotencyKey
      return payload
    },
    async runPreview() {
      if (!this.canPreview) return
      this.previewLoading = true
      this.errorText = ''
      this.applyResult = null
      this.idempotencyKey = this.makeIdempotencyKey()
      try {
        this.preview = await notificationTaskGroupApi.previewProductionSettings(GROUP_ID, this.intent(true))
        this.previewFingerprint = this.fingerprint
      } catch (error) {
        this.preview = null
        this.errorText = this.readError(error, '设置预览失败。')
      } finally {
        this.previewLoading = false
      }
    },
    async saveSettings() {
      if (!this.canApply) return
      try {
        await this.$confirm('保存后由独立调度进程自动同步，不会立即运行任务或发送飞书。是否继续？', '确认保存持仓监控设置', {
          confirmButtonText: '确认保存',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (error) {
        return
      }
      this.applyLoading = true
      this.errorText = ''
      try {
        this.applyResult = await notificationTaskGroupApi.applySettings(GROUP_ID, {
          ...this.intent(true),
          preview_id: this.preview.preview_id,
          preview_digest: this.preview.preview_digest,
          preview_created_at: this.preview.preview_created_at,
          preview_expires_at: this.preview.preview_expires_at,
          before_group_revision: this.preview.before_group_revision,
          confirm_apply: true
        })
        this.$message.success('持仓监控设置已保存')
        this.$emit('saved', this.applyResult)
        this.preview = null
        this.previewFingerprint = ''
        await this.loadOptions()
      } catch (error) {
        this.errorText = this.readError(error, '保存设置失败，未确认任何部分变更。')
      } finally {
        this.applyLoading = false
      }
    },
    readError(error, fallback) {
      const detail = error && error.response && error.response.data ? error.response.data.detail : null
      const code = detail && typeof detail === 'object' ? detail.code : ''
      const message = detail && typeof detail === 'object' ? detail.message : detail
      const map = {
        POSITION_CONTEXT_NOTIFICATION_POLICY_LOCKED: '持仓监控必须保持“仅异常通知”。',
        ACTIVE_GROUP_REQUIRES_SCHEDULE: '启用时至少需要一个运行时间。',
        PARENT_CHILD_SET_MISMATCH: '持仓监控任务行缺失或重复，已阻止保存。',
        PREVIEW_EXPIRED: '设置预览已过期，请重新预览。',
        GROUP_REVISION_MISMATCH: '后台设置已变化，请重新加载并预览。'
      }
      return map[code] || (typeof message === 'string' && message ? message : fallback)
    },
    displayTime(value) { return value ? String(value).replace('T', ' ').slice(0, 19) : '-' }
  }
}
</script>

<style scoped>
.drawer-shell{height:100%;display:flex;flex-direction:column;background:#fff}.drawer-header{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;padding:22px 24px 18px;border-bottom:1px solid #e8edf4;background:#f8fafc}.drawer-kicker{color:#f59e0b;font-size:12px;font-weight:700}.drawer-header h2{margin:7px 0 6px;color:#0f172a;font-size:22px}.drawer-header p{margin:0;color:#64748b;font-size:13px;line-height:1.6}.drawer-close{color:#64748b}.drawer-body{flex:1;min-height:0;overflow-y:auto;padding:20px 24px 28px}.block{margin-bottom:16px}.time-editor{display:flex;align-items:center;gap:10px}.time-tags{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px}.empty-tip,.form-tip,.actions span{color:#94a3b8;font-size:12px}.form-tip{margin-top:8px;line-height:1.6}.actions{display:flex;align-items:center;gap:12px;margin-top:18px}.result{margin-top:18px}@media(max-width:760px){.drawer-header,.drawer-body{padding-left:16px;padding-right:16px}}
</style>
